import styles from './DistributionBars.module.css'

interface DistributionBarsProps {
  /** 16 bucket counts (bell curve); bars scale to the tallest bucket. */
  distribution: number[]
  /** Highlighted bucket index; null hides the marker and highlight. */
  productBucket: number | null
  /** Floating value-pill text above the product's bucket; null hides the pill. */
  markerText: string | null
  axisMin: string
  axisMax: string
  /** Tighter vertical footprint (featured-scores card); default is the ranks-card size. */
  compact?: boolean
}

/** The bare 16-bucket bar row (bell curve). Kept separate from the marker/axis wrapper
 *  below so the wrapper owns the floating value pill (the `.45s` left/height transitions
 *  slide the marker and morph the curve when the chart's distribution changes). */
const Bars: React.VFC<{ distribution: number[]; productBucket: number | null }> = ({
  distribution,
  productBucket,
}) => {
  const max = Math.max(1, ...distribution)
  return (
    <div className={styles.bars}>
      {distribution.map((count, i) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          className={styles.bar}
          data-active={i === productBucket}
          data-filled={productBucket !== null && i < productBucket}
          style={{ height: `${(count / max) * 100}%` }}
        />
      ))}
    </div>
  )
}

/**
 * Self-contained distribution chart: floating value marker + bars + min/max axis
 * row (the featured-scores card's shape). The .45s left/height transitions make
 * the marker slide and the bell curve morph when the same mounted chart switches
 * distributions — keep it mounted across switches, and keep the CSS transitions.
 */
const DistributionBars: React.VFC<DistributionBarsProps> = ({
  distribution,
  productBucket,
  markerText,
  axisMin,
  axisMax,
  compact,
}) => {
  const markerLeft =
    productBucket !== null && markerText !== null
      ? ((productBucket + 0.5) / distribution.length) * 100
      : null
  return (
    <>
      <div className={styles.chart} data-compact={compact || undefined}>
        {markerLeft !== null && (
          <span className={styles.valueFloat} style={{ left: `${markerLeft}%` }}>
            {markerText}
          </span>
        )}
        <Bars distribution={distribution} productBucket={productBucket} />
      </div>
      <div className={styles.axis} data-compact={compact || undefined}>
        <span>{axisMin}</span>
        <span>{axisMax}</span>
      </div>
    </>
  )
}

export default DistributionBars
